import { createSlice } from '@reduxjs/toolkit';

/**
 * Floating room cards (plan docs/plans/floating-room-cards/PLAN.md v0.19, row 6). Dark behind `credentials.roomStays`.
 *
 * Holds the org's designated holders (to know whether a person is a room), a room's stays for its person drawer, and the
 * pending issue confirmations, one per card: the server refused an assign to a room with ROOM_STAY_CONFIRMATION_REQUIRED,
 * ROOM_STAY_STALE or NO_APPLICABLE_STAY and sent the preview; the existing assign is re-sent with the chosen stay's ID and
 * revision (R15, INV-10). Several cards saved at once each get their own confirmation, shown in turn, so none is dropped
 * silently (DA1-01). Nothing here decides access — the server does.
 */

const initialState = {
	holders: [],
	staysError: null,
	lastChange: null,
	holdersLoaded: false,
	holdersLoading: false,
	holdersError: null,
	staysByHolder: {},
	staysLoading: false,
	mutating: false,
	error: null,
	/** Hotel guest mode: the room holder whose issue is running, and how the last one ended ({ ok, error }). */
	issuing: null,
	issueResult: null,
	/** [{ credentialID, data, reason, preview, message }] — one per card awaiting a stay confirmation, oldest first. */
	issueConfirmations: [],
	/** People page "Show card holders" toggle (room-cards-setup R6): holders are hidden from lists unless this is on. */
	showHolders: false,
	/**
	 * Room stays page (room-cards-setup PLAN.md §5.2, rows 9 and 11a), keyed by siteID. Every delivery state here is
	 * the server's (INV-10); nothing in the Dashboard derives one.
	 */
	board: { bySite: {}, loading: false, error: null },
	// `error` is the last load's; `errorBySite` is each site's own (a load for one site never speaks for another).
	setup: { bySite: {}, loading: false, error: null, errorBySite: {} },
	occupancy: { bySite: {}, reading: false, error: null },
	panelUsers: { bySite: {}, loading: false, error: null },
	/** The last preview/apply answer and the exact body it answered, so Apply is never enabled for a different draft. */
	setupRun: { running: null, body: null, result: null, error: null },
	/** Add room after setup (PLAN.md §5.1.9, D-16): as setupRun, for one site's single room body. */
	addRoomRun: { running: null, siteID: null, body: null, result: null, error: null },
	/** credentialID → true while that card's repair runs; holderID → true while that holder's retirement runs. */
	repairing: {},
	retiring: {}
};

const slice = createSlice({
	name: 'roomStays',
	initialState,
	reducers: {
		loadHolders: (state) => {
			state.holdersLoading = true;
			state.holdersError = null;
			state.error = null;
		},
		loadHoldersSuccess: (state, { payload }) => {
			state.holders = payload || [];
			state.holdersLoaded = true;
			state.holdersLoading = false;
		},
		loadHoldersError: (state, { payload }) => {
			state.holdersLoading = false;
			state.holdersLoaded = true;
			state.holdersError = payload || 'Room holders could not be loaded.';
			state.error = payload;
		},
		loadStays: (state) => {
			state.staysLoading = true;
			state.staysError = null;
			state.error = null;
		},
		loadStaysSuccess: (state, { payload: { holderID, stays } }) => {
			state.staysByHolder[holderID] = stays || [];
			state.staysLoading = false;
		},
		loadStaysError: (state, { payload }) => {
			state.staysLoading = false;
			// Its own field: a failed READ must never read as a refused CHANGE (lastChange below).
			state.staysError = payload || 'Failed to load stays.';
			state.error = payload;
		},
		/** PATCH (endAt) or cancel at the stay's revision; the saga reloads the room's stays either way. */
		changeStay: (state) => {
			state.mutating = true;
			state.error = null;
			state.lastChange = null;
		},
		/**
		 * `lastChange` is the server's answer to the last stay change, kept apart from the reload that follows: the
		 * reload can fail, or not have landed yet, and neither says anything about whether the change was accepted.
		 */
		changeStayDone: (state, { payload }) => {
			state.mutating = false;
			state.error = payload?.error ?? null;
			state.lastChange =
				payload?.roomStayID != null
					? {
							roomStayID: Number(payload.roomStayID),
							ok: !payload.error,
							error: payload.error ?? null,
							operation: payload.operation ?? null
						}
					: null;
		},
		/** POST /room-stays/stays (manual stay); `thenAssign` re-sends a card's assign once the stay exists. */
		createStay: (state) => {
			state.mutating = true;
			state.error = null;
		},
		createStayDone: (state, { payload }) => {
			state.mutating = false;
			state.error = payload?.error ?? null;
		},
		/**
		 * Manage Access "Hotel guest" mode: save the stay (new, or its changed dates) and issue the chosen cards to the
		 * room against it, all from one review. `issuing` is the room holder's ID while it runs.
		 */
		issueForStay: (state, { payload }) => {
			state.issuing = payload.holderID;
			state.issueResult = null;
		},
		issueForStayDone: (state, { payload }) => {
			state.issuing = null;
			state.issueResult = payload || null;
		},
		clearIssueResult: (state) => {
			state.issueResult = null;
		},
		/** Adds this card's confirmation, or refreshes it in place (a stale preview comes back for the same card). */
		requestIssueConfirmation: (state, { payload }) => {
			const at = state.issueConfirmations.findIndex((c) => c.credentialID === payload.credentialID);
			if (at >= 0) state.issueConfirmations[at] = payload;
			else state.issueConfirmations.push(payload);
		},
		setShowHolders: (state, { payload }) => {
			state.showHolders = Boolean(payload);
		},
		loadBoard: (state) => {
			state.board.loading = true;
			state.board.error = null;
		},
		loadBoardDone: (state, { payload: { siteID, data, error = null } }) => {
			state.board.loading = false;
			state.board.error = error;
			if (data) state.board.bySite[siteID] = data;
		},
		/** All sites (PLAN v0.8.5 §5.2): several sites' boards in one go; each lands through loadBoardsDone. */
		loadBoards: (state) => {
			state.board.loading = true;
			state.board.error = null;
		},
		loadBoardsDone: (state, { payload: { boards = [], errors = [] } }) => {
			state.board.loading = false;
			state.board.error = errors.length ? errors.join(' ') : null;
			boards.forEach(({ siteID, data }) => {
				state.board.bySite[siteID] = data;
			});
		},
		loadSetup: (state, { payload } = {}) => {
			state.setup.loading = true;
			state.setup.error = null;
			if (payload?.siteID != null) {
				state.setup.errorBySite = state.setup.errorBySite || {};
				delete state.setup.errorBySite[payload.siteID];
			}
		},
		loadSetupDone: (state, { payload: { siteID, data, error = null } }) => {
			state.setup.loading = false;
			state.setup.error = error;
			state.setup.errorBySite = state.setup.errorBySite || {};
			if (error) state.setup.errorBySite[siteID] = error;
			else delete state.setup.errorBySite[siteID];
			if (data) state.setup.bySite[siteID] = data;
		},
		loadOccupancy: () => {},
		readOccupancy: (state) => {
			state.occupancy.reading = true;
			state.occupancy.error = null;
		},
		occupancyDone: (state, { payload: { siteID, locks, error = null } }) => {
			state.occupancy.reading = false;
			state.occupancy.error = error;
			if (locks) state.occupancy.bySite[siteID] = locks;
		},
		loadPanelUsers: (state) => {
			state.panelUsers.loading = true;
			state.panelUsers.error = null;
		},
		loadPanelUsersDone: (state, { payload: { siteID, data, error = null } }) => {
			state.panelUsers.loading = false;
			state.panelUsers.error = error;
			if (data) state.panelUsers.bySite[siteID] = data;
		},
		/** payload { mode: 'preview'|'apply', body } — `body` carries setupKey on apply. */
		runSetup: (state, { payload }) => {
			state.setupRun.running = payload.mode;
			state.setupRun.error = null;
		},
		// `keepPreview`: an apply that got no answer (transport failure, 5xx) leaves the preview that enabled it in
		// place, so Apply stays enabled for the same body and its resend carries the same setup key (a replay, R4).
		runSetupDone: (state, { payload: { body, result = null, error = null, keepPreview = false } }) => {
			const preview = keepPreview && state.setupRun.result?.mode === 'preview' ? state.setupRun.result : null;
			state.setupRun = { running: null, body, result: preview || result, error };
		},
		/** payload { mode: 'preview'|'apply', siteID, body } — `body` carries roomKey on apply. */
		runAddRoom: (state, { payload }) => {
			state.addRoomRun.running = payload.mode;
			state.addRoomRun.error = null;
		},
		// `keepPreview` as runSetupDone: an unanswered apply keeps Apply enabled, so a resend replays the same roomKey.
		runAddRoomDone: (state, { payload: { siteID, body, result = null, error = null, keepPreview = false } }) => {
			const prev = state.addRoomRun.result;
			const preview = keepPreview && prev?.mode === 'preview' ? prev : null;
			state.addRoomRun = { running: null, siteID, body, result: preview || result, error };
		},
		clearAddRoom: (state) => {
			state.addRoomRun = initialState.addRoomRun;
		},
		repairCard: (state, { payload }) => {
			state.repairing[payload.credentialID] = true;
		},
		repairCardDone: (state, { payload }) => {
			delete state.repairing[payload.credentialID];
		},
		retireHolder: (state, { payload }) => {
			state.retiring[payload.holderID] = true;
		},
		retireHolderDone: (state, { payload }) => {
			delete state.retiring[payload.holderID];
		},
		/** Removes ONE card's confirmation (confirmed, issued or cancelled); every other card's stays queued. */
		dismissIssueConfirmation: (state, { payload }) => {
			state.issueConfirmations = state.issueConfirmations.filter((c) => c.credentialID !== payload?.credentialID);
		}
	},
	extraReducers: (builder) => {
		builder.addCase('auth/logout', () => initialState);
		// An org switch must not carry org A's queued confirmations or stays into org B (DB2-04).
		builder.addCase('auth/switchProfileSuccess', () => initialState);
	}
});

export const roomStaysActions = slice.actions;
export default slice.reducer;
